import { useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppState } from '../../hooks/useAppState';
import { useUser } from '../../context/UserContext';
import { useNotification } from '../../context/NotificationContext';

// Routes that require specific permissions
const ROUTE_PERMISSIONS = {
  // Product routes - admin/super_admin only
  '/products': 'products',
  '/addproduct': 'products',
  '/bulkuploadproducts': 'products',
  '/masterproducts': 'products',
  // Settings routes - admin/super_admin only
  '/settings/organization': 'admin',
  '/settings/locations': 'admin',
  '/settings/users': 'admin',
  // Super admin only routes
  '/settings/organizations': 'super_admin',
  '/settings/admin-tokens': 'super_admin',
  '/settings/universal-products': 'super_admin',
  // Template creation - admin/super_admin only (agents can view but not create)
  '/flyertemplatemanager': 'admin',
  '/adtemplatemanager': 'admin',
};

/**
 * ProtectedRoute - Auth guard component with permission checks
 * Uses centralized useAppState hook and UserContext for permissions
 */
const ProtectedRoute = ({ component: Component, children }) => {
  const { isAuthenticated, user } = useAuth0();
  const navigate = useNavigate();
  const location = useLocation();
  const notification = useNotification();

  // Get all state from centralized hook
  const {
    needsOnboarding,
    isProfileLoading,
    isInitialized,
  } = useAppState();

  // Check if email is verified - redirect to verification page if not
  useEffect(() => {
    if (isAuthenticated && user && user.email_verified === false) {
      navigate('/email-verification-pending', { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  // Get permission helpers
  const { isAdmin, isSuperAdmin, canManageProducts, isLoading: isUserLoading } = useUser();

  // Check if current route requires specific permissions
  const checkRoutePermission = () => {
    const path = location.pathname;

    // Find matching route permission
    for (const [route, permission] of Object.entries(ROUTE_PERMISSIONS)) {
      if (path.startsWith(route)) {
        if (permission === 'super_admin') {
          return isSuperAdmin();
        }
        if (permission === 'admin') {
          return isAdmin();
        }
        if (permission === 'products') {
          return canManageProducts();
        }
      }
    }

    // No specific permission required
    return true;
  };

  // Handle redirects based on onboarding status
  useEffect(() => {
    if (!isProfileLoading && isInitialized && isAuthenticated) {
      const isOnWelcomePage = location.pathname === '/welcome';

      if (needsOnboarding && !isOnWelcomePage) {
        // User needs onboarding - redirect to welcome
        navigate('/welcome');
      } else if (!needsOnboarding && isOnWelcomePage) {
        // User completed onboarding but is on welcome page - redirect to dashboard
        navigate('/dashboard');
      }
    }
  }, [
    isProfileLoading,
    isInitialized,
    isAuthenticated,
    needsOnboarding,
    navigate,
    location.pathname,
  ]);

  // Handle permission-based redirects
  useEffect(() => {
    if (!isProfileLoading && !isUserLoading && isInitialized && isAuthenticated) {
      if (!checkRoutePermission()) {
        notification.error('You do not have permission to access this page');
        navigate('/dashboard');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfileLoading, isUserLoading, isInitialized, isAuthenticated, location.pathname]);

  // Show nothing while loading or not authenticated or email not verified
  if (isProfileLoading || isUserLoading || !isAuthenticated || !user || !isInitialized) {
    return null;
  }

  // Don't render if email is not verified (redirect handled in useEffect above)
  if (user.email_verified === false) {
    return null;
  }

  // Check permissions before rendering
  if (!checkRoutePermission()) {
    return null;
  }

  // Prefer stable children (a JSX element whose type is preserved across
  // renders). Passing `component={() => (...)}` creates a NEW component type on
  // every render, which forces React to unmount/remount the whole subtree —
  // that caused the dashboard to remount in a loop and storm the API. `children`
  // avoids that; `component` is kept for stable, module-level components.
  if (children) {
    return children;
  }

  return Component ? <Component /> : null;
};

export default ProtectedRoute;
